/**
 * RemedialRepairsBuilderTab — Iter157 Phase A.
 *
 * Lightweight leak-area-driven repairs builder. Each leak area is a
 * structured card with: location · description · open notes · a small
 * inline allocator (Type / Qty / Length / rates) sourced from the same
 * Component rate catalogue the main Components tab uses.
 *
 * Customer-facing PDF (later in this iteration) emits each leak area
 * as a separately-priced line on a checklist; client picks which to
 * authorise.
 *
 * Why a focused mini-allocator vs the full AppraisalComponentsTab UI:
 *   • Each Remedial area is typically 1-5 component lines (small qty
 *     of tiles / 1m of ridging / 1 dektite replacement). The full
 *     Components tab is overkill for that volume and overwhelms the
 *     operator inside a leak-area card.
 *   • We still pull the SAME `rates` catalogue so $$ stay consistent
 *     with the rest of the appraisal — no parallel catalogue.
 *   • An "Open in full Components editor" escape hatch can be added
 *     later if an area grows beyond a handful of lines.
 */
import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import SaveDocButton from './SaveDocButton';
import {
  Loader2, Plus, Trash2, Save, MapPin, FileText, AlertTriangle, Settings,
  CheckCircle2, ChevronDown, ChevronUp, Circle, Check, X, Edit3,
} from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import LeakAreaPhotoStrip from '@/components/LeakAreaPhotoStrip';
import BuilderRoofDetailsPanel from '@/components/BuilderRoofDetailsPanel';

// Iter161 — REPAIR_TYPE_PRESETS is now a FALLBACK only. The runtime
// list is loaded from /api/remedial-repairs/presets (editable via
// /admin/repair-presets). This local map stays as a safety net when
// the backend is unreachable so the dropdown is never empty.
const REPAIR_TYPE_PRESETS = {
  Concrete: ['Replace defect tiles', 'Band and Point ridging', 'Replace cracked ridge section', 'Replace pipe flashing', 'Reseal Dektite'],
  Iron:     ['Replace rusty sheet', 'Upgrade pipe flashing (backtray + dektite)', 'Replace nails with screws', 'Replace damaged ridge section', 'Replace defect flashing'],
  'Decramastic': ['Replace defect section', 'Reseal joints', 'Replace pipe flashing'],
  'ColourTile': ['Replace defect tiles', 'Replace ridging section', 'Replace pipe flashing'],
  Other:    ['Custom repair'],
};

const fmt = (n) => `$${(Number(n) || 0).toFixed(2)}`;

export default function RemedialRepairsBuilderTab({ api, appraisalId, appraisal, onJumpToPin }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [doc, setDoc] = useState(null);
  const [rates, setRates] = useState({});
  const [roofType, setRoofType] = useState('');
  const [expanded, setExpanded] = useState({}); // {area_id: bool}
  // Iter161 — Editable preset lists (Repair Type / Location / Description).
  // Loaded from /api/remedial-repairs/presets; managed via the admin
  // page at /admin/repair-presets. cascadeOverride flips the cascade
  // OFF per leak area so the operator sees the FULL list.
  const [presetsRT, setPresetsRT] = useState([]);   // repair_type
  const [presetsLoc, setPresetsLoc] = useState([]); // location
  const [presetsDesc, setPresetsDesc] = useState([]); // description
  const [cascadeOverride, setCascadeOverride] = useState({}); // {area_id: bool} — show all instead of cascaded subset
  // Iter161 — Repair-line Component Type cascade override (per line)
  // and Repair-line Roof-Type/Profile/Material cascade override toggle.
  const [lineCascadeOverride, setLineCascadeOverride] = useState({}); // {line_id: bool}
  // Iter161 Phase B — Leak Area pins. Loaded from the appraisal's
  // /diagrams/leak-area pin slot OR from the legacy roof_plan slot.
  // Each leak_area row in the doc has a pin_id field; when set, we
  // surface the pin's x_pct/y_pct so the operator sees it's placed.
  const [diagramPins, setDiagramPins] = useState([]);
  const [placingPinFor, setPlacingPinFor] = useState(null); // area_id currently in "drop pin" handoff
  // Where on the appraisal do leak pins live? The annotator persists
  // its pins via `/diagrams/{appraisal_id}/{slot}`; the default Roof
  // Plan slot is 'roof_plan'. Leak pins go in 'roof_plan' too so they
  // render alongside Ridge/Valley/etc. on the same diagram.
  const PIN_SLOT = 'roof_plan';

  // Load doc + rates + roof type
  const load = useCallback(async () => {
    setLoading(true);
    try {
      // `/component-rates` returns {summary, work_types}; per-type rows
      // live behind `?component_type=X`. Fetch the doc + the work_types
      // index + the 3 preset lists in parallel, then fan out one rates
      // request per work type.
      const [docRes, idxRes, rtRes, locRes, descRes, pinsRes] = await Promise.all([
        api.get(`/remedial-repairs/appraisals/${appraisalId}`),
        api.get('/component-rates'),
        api.get('/remedial-repairs/presets?kind=repair_type').catch(() => ({ data: { presets: [] } })),
        api.get('/remedial-repairs/presets?kind=location').catch(() => ({ data: { presets: [] } })),
        api.get('/remedial-repairs/presets?kind=description').catch(() => ({ data: { presets: [] } })),
        // Iter161 Phase B — load Roof Plan diagram pins so the
        // builder can show "placed at X%, Y%" chips per leak area
        // (and surface a Remove-pin action). Pins live on
        // appraisal.roof_diagram_annotations filtered by slot.
        api.get(`/appraisals/${appraisalId}`).catch(() => ({ data: {} })),
      ]);
      setDoc(docRes.data);
      setPresetsRT(rtRes.data?.presets || []);
      setPresetsLoc(locRes.data?.presets || []);
      setPresetsDesc(descRes.data?.presets || []);
      const allPins = (pinsRes.data?.roof_diagram_annotations || []).filter(p =>
        (p.slot || 'roof_plan') === PIN_SLOT
      );
      setDiagramPins(allPins);
      const workTypes = idxRes.data?.work_types || [];
      const perType = await Promise.all(
        workTypes.map(wt =>
          api.get(`/component-rates?component_type=${encodeURIComponent(wt)}`)
            .then(r => [wt, r.data || []])
            .catch(() => [wt, []])
        )
      );
      const grouped = {};
      perType.forEach(([wt, rows]) => { grouped[wt] = rows; });
      setRates(grouped);
      // Pick up roof type from appraisal (if present) for preset filtering.
      // Iter161 fix — fall back to appraisal.roof_type then to a
      // subtype→base mapping so the cascade still filters when the
      // operator hasn't captured roof_details yet.
      const propId = appraisal?.property_id;
      let resolvedRT = '';
      if (propId) {
        try {
          const rdRes = await api.get(`/roof-details/${propId}`);
          const rd0 = (rdRes.data || [])[0] || {};
          resolvedRT = rd0.roof_type || '';
        } catch (e) { /* no roof yet */ }
      }
      if (!resolvedRT) resolvedRT = appraisal?.roof_type || '';
      if (!resolvedRT) {
        const st = (appraisal?.roof_subtype || '').toLowerCase();
        if (st.includes('concrete') || st.includes('tile') && !st.includes('colour') && !st.includes('tin'))      resolvedRT = 'Concrete';
        else if (st.includes('iron') || st.includes('coloursteel') || st.includes('longrun') || st.includes('galv')) resolvedRT = 'Iron';
        else if (st.includes('decramastic') || st.includes('decra'))                                               resolvedRT = 'Decramastic';
        else if (st.includes('colour') || st.includes('color') || st.includes('tin'))                              resolvedRT = 'ColourTile';
      }
      setRoofType(resolvedRT);
      // Auto-expand the most recently edited area on load
      const areas = docRes.data?.leak_areas || [];
      if (areas.length > 0) {
        setExpanded({ [areas[areas.length - 1].area_id]: true });
      }
    } catch (e) {
      toast.error('Failed to load Remedial Repairs');
    } finally {
      setLoading(false);
    }
  }, [api, appraisalId, appraisal]);

  useEffect(() => { load(); }, [load]);

  // Iter161 Phase B — When the annotator drops a leak pin in response
  // to our `rtr:start-leak-pin-drop` event, it dispatches
  // `rtr:leak-pin-placed`. By the time the operator returns to the
  // Remedial Repairs tab, the component remounts and load() re-fetches
  // the diagram pins — the pin carries `leak_area_id` so findPin()
  // can match it. So no per-pin persistence is needed here; we just
  // listen so we can show a toast confirmation (and refresh the
  // local diagram pins cache if we're still mounted, which is rare).
  useEffect(() => {
    const handler = async () => {
      try {
        const { data } = await api.get(`/appraisals/${appraisalId}`);
        const all = (data.roof_diagram_annotations || []).filter(p => (p.slot || 'roof_plan') === PIN_SLOT);
        setDiagramPins(all);
      } catch (_) { /* ignore */ }
      setPlacingPinFor(null);
      toast.success('Leak pin placed on diagram');
    };
    window.addEventListener('rtr:leak-pin-placed', handler);
    return () => window.removeEventListener('rtr:leak-pin-placed', handler);
  }, [api, appraisalId]);

  // Find the pin row matching a leak_area's id.
  // Iter161 Phase B — match by `leak_area_id` (the pin carries it
  // back-reference) — more robust than matching pin.id since the
  // annotator generates its own pin ids and we don't always have a
  // chance to round-trip them back onto the leak_area row.
  const findPin = (area) => {
    if (!area?.area_id) return null;
    return diagramPins.find(p => p.leak_area_id === area.area_id) || null;
  };

  const startPlacingPin = async (area) => {
    if (!onJumpToPin) {
      toast.error('Roof Plan navigation not wired — cannot place pin');
      return;
    }
    // Iter161 Phase B — persist any local edits BEFORE navigating
    // away. The component unmounts when the subtab changes, so the
    // leak-pin-placed event listener wouldn't survive a click on the
    // diagram. By saving here we make sure (a) the leak_area is in
    // the DB so the round-trip works, and (b) the post-drop reload
    // shows the chip.
    try {
      await api.put(`/remedial-repairs/appraisals/${appraisalId}`, {
        leak_areas: doc?.leak_areas || [],
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save leak area before navigating');
      return;
    }
    setPlacingPinFor(area.area_id);
    onJumpToPin(null, null);
    setTimeout(() => {
      try {
        window.dispatchEvent(new CustomEvent('rtr:start-leak-pin-drop', {
          detail: {
            leak_area_id: area.area_id,
            label: area.label || '',
            area_label: area.label || area.location_text || `Area ${area.area_id?.slice(0, 4)}`,
          },
        }));
      } catch (_) { /* ignore */ }
    }, 450);
  };

  const removePin = async (area) => {
    const pin = findPin(area);
    if (!pin?.id) return;
    if (!window.confirm(`Remove the diagram pin linked to "${area.label || 'this leak area'}"?`)) return;
    try {
      // Fetch full set of pins, drop the leak pin, persist via PATCH.
      const { data: appr } = await api.get(`/appraisals/${appraisalId}`);
      const remaining = (appr.roof_diagram_annotations || []).filter(p => p.id !== pin.id);
      await api.patch(`/appraisals/${appraisalId}/roof-diagram-annotations`, { annotations: remaining });
      const inSlot = remaining.filter(p => (p.slot || 'roof_plan') === PIN_SLOT);
      setDiagramPins(inSlot);
      // Iter162 fix — force a full reload so any stale denormalised
      // pin_x_pct/pin_y_pct on the leak_areas (from earlier iter161 PUT
      // cycles) don't keep showing the chip after the pin is gone.
      await load();
      toast.success('Diagram pin removed');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to remove pin');
    }
  };

  // Computed: ungrouped rates list (for the type-name picker)
  const allRates = useMemo(() => {
    const out = [];
    Object.entries(rates).forEach(([cat, rows]) => {
      rows.forEach(r => out.push({ ...r, _category: cat }));
    });
    return out;
  }, [rates]);

  // Iter161 — Cascaded option lists. Each computed from the editable
  // preset collection, falling back to local REPAIR_TYPE_PRESETS only
  // when the backend list is empty (initial app boot before seed).
  // `cascadeOverride[area_id]` flips the cascade off per leak area so
  // the operator sees the FULL list (use case: cross-roof-type repair).
  const repairTypePresetsFor = useCallback((area_id) => {
    const override = !!cascadeOverride[area_id];
    if (presetsRT.length > 0) {
      const all = presetsRT.map(p => p.name);
      if (override || !roofType) return Array.from(new Set(all));
      // Filter: parent_roof_type matches OR is empty (global)
      const filtered = presetsRT
        .filter(p => !p.parent_roof_type || p.parent_roof_type === roofType)
        .map(p => p.name);
      return Array.from(new Set(filtered));
    }
    // Fallback to local presets (covers an unseeded backend)
    if (override) return Array.from(new Set(Object.values(REPAIR_TYPE_PRESETS).flat()));
    if (REPAIR_TYPE_PRESETS[roofType]) return REPAIR_TYPE_PRESETS[roofType];
    return Array.from(new Set(Object.values(REPAIR_TYPE_PRESETS).flat()));
  }, [presetsRT, roofType, cascadeOverride]);

  const locationPresets = useMemo(
    () => Array.from(new Set(presetsLoc.map(p => p.name))),
    [presetsLoc]
  );

  // Description cascade: filter by parent_repair_type, or global ones,
  // when an area has a repair_type set. Override returns the full list.
  const descriptionPresetsFor = useCallback((area_id, repair_type) => {
    const override = !!cascadeOverride[area_id];
    if (override || !repair_type) {
      return Array.from(new Set(presetsDesc.map(p => p.name)));
    }
    const matched = presetsDesc.filter(p => !p.parent_repair_type || p.parent_repair_type === repair_type);
    return Array.from(new Set(matched.map(p => p.name)));
  }, [presetsDesc, cascadeOverride]);

  // Inline "+ Add new …" handler that POSTs a new preset on the fly
  // and re-syncs the local list. Used when an operator types a value
  // that's not in the dropdown and wants to save it for next time.
  const inlineAddPreset = async (kind, name, extras = {}) => {
    const clean = (name || '').trim();
    if (!clean) return;
    try {
      const { data } = await api.post('/remedial-repairs/presets', {
        kind, name: clean,
        parent_roof_type:   extras.parent_roof_type   || '',
        parent_repair_type: extras.parent_repair_type || '',
        sort_order: 999,
      });
      if (kind === 'repair_type')       setPresetsRT(p => [...p, data]);
      else if (kind === 'location')     setPresetsLoc(p => [...p, data]);
      else if (kind === 'description')  setPresetsDesc(p => [...p, data]);
      toast.success(`Added preset "${clean}"`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Failed to add ${kind} preset`);
    }
  };

  // ─── Mutation helpers (work on a local copy then PUT on save) ───
  const updateDoc = (patch) => setDoc(d => ({ ...d, ...patch }));
  const updateArea = (area_id, patch) => {
    setDoc(d => ({
      ...d,
      leak_areas: d.leak_areas.map(a => a.area_id === area_id ? { ...a, ...patch } : a),
    }));
  };
  const addArea = () => {
    const newArea = {
      area_id: crypto.randomUUID ? crypto.randomUUID() : `area_${Date.now()}`,
      label: `Area ${(doc.leak_areas?.length || 0) + 1}`,
      location_text: '', pin_id: '', description: '', notes: '',
      repair_type: '', repair_lines: [], photo_ids: [],
      client_status: 'pending', area_total_customer: 0, area_total_cost: 0,
    };
    setDoc(d => ({ ...d, leak_areas: [...(d.leak_areas || []), newArea] }));
    setExpanded(e => ({ ...e, [newArea.area_id]: true }));
  };
  const removeArea = (area_id) => {
    if (!window.confirm('Remove this leak area? Any allocated repair lines will be lost.')) return;
    setDoc(d => ({ ...d, leak_areas: d.leak_areas.filter(a => a.area_id !== area_id) }));
  };
  const addLine = (area_id) => {
    setDoc(d => ({
      ...d,
      leak_areas: d.leak_areas.map(a => a.area_id === area_id ? {
        ...a,
        repair_lines: [...(a.repair_lines || []), {
          line_id: crypto.randomUUID ? crypto.randomUUID() : `line_${Date.now()}`,
          // Iter161 — component_type/roof_type/profile/material now
          // act as cascade keys that narrow the Type dropdown. They
          // are auto-populated when the operator picks a Type, and can
          // be set manually to filter the list before picking.
          component_type: '', type_name: '', profile: '', material: '',
          roof_type: roofType || '',
          quantity: 1, length: 0,
          buy_cost: 0, materials_rate: 0, labour_rate: 0, contractor_rate: 0,
          supplier: '', supplier_code: '', materials_desc: '', labour_details: '', notes: '',
        }],
      } : a),
    }));
  };
  const updateLine = (area_id, line_id, patch) => {
    setDoc(d => ({
      ...d,
      leak_areas: d.leak_areas.map(a => a.area_id === area_id ? {
        ...a,
        repair_lines: (a.repair_lines || []).map(l => l.line_id === line_id ? { ...l, ...patch } : l),
      } : a),
    }));
  };
  const removeLine = (area_id, line_id) => {
    setDoc(d => ({
      ...d,
      leak_areas: d.leak_areas.map(a => a.area_id === area_id ? {
        ...a,
        repair_lines: (a.repair_lines || []).filter(l => l.line_id !== line_id),
      } : a),
    }));
  };
  // Iter161 — Cascaded rate filter for a repair line. Cascade order
  // (full): component_type → roof_type → profile → material → type_name.
  // Each filter step ONLY applies when the line has a value for that
  // field. Operator can flip the override (lineCascadeOverride[line_id])
  // to see the full Type list regardless of cascade.
  const cascadedRatesFor = useCallback((line) => {
    const override = !!lineCascadeOverride[line.line_id];
    let pool = allRates;
    if (!override && line.component_type) {
      pool = pool.filter(r => (r._category || r.component_type || '').toLowerCase() === line.component_type.toLowerCase());
    }
    if (!override && line.roof_type) {
      pool = pool.filter(r => !r.roof_type || r.roof_type === line.roof_type);
    }
    if (!override && line.profile) {
      pool = pool.filter(r => !r.profile || r.profile === line.profile);
    }
    if (!override && line.material) {
      pool = pool.filter(r => !r.material || r.material === line.material);
    }
    return pool;
  }, [allRates, lineCascadeOverride]);

  // Stamp rate fields onto a line when the operator picks a type
  const pickTypeName = (area_id, line_id, type_name) => {
    const rate = allRates.find(r => r.type_name === type_name);
    if (!rate) return updateLine(area_id, line_id, { type_name });
    updateLine(area_id, line_id, {
      type_name,
      component_type: rate._category || rate.component_type || '',
      // Iter161 — keep cascade keys in sync so subsequent edits cascade
      // from the picked Type rather than reset to the unfiltered list.
      roof_type: rate.roof_type || roofType || '',
      profile: rate.profile || '',
      material: rate.material || '',
      buy_cost: Number(rate.buy_cost) || 0,
      materials_rate: Number(rate.materials_rate) || 0,
      labour_rate: Number(rate.labour_rate) || 0,
      contractor_rate: Number(rate.contractor_rate) || 0,
      supplier: rate.supplier || '',
      supplier_code: rate.supplier_code || '',
      materials_desc: rate.materials_desc || '',
      labour_details: rate.labour_details || '',
    });
  };

  // Iter161 — Component Type list for the cascade picker. Built from
  // the rate catalogue's distinct work_type keys.
  const componentTypeOptions = useMemo(
    () => Array.from(new Set(Object.keys(rates))).sort(),
    [rates]
  );

  // Live line total (mirror of backend _calc_totals — for display only)
  const lineTotal = (l) => {
    const qty = Number(l.quantity) || 0;
    const len = Number(l.length) || 0;
    const units = len > 0 ? qty * len : qty;
    const mat = (Number(l.materials_rate) || 0) * units;
    const lab = (Number(l.labour_rate) || 0) * units;
    const con = (Number(l.contractor_rate) || 0) * units;
    return mat + lab + con;
  };
  const areaTotal = (a) => (a.repair_lines || []).reduce((s, l) => s + lineTotal(l), 0);
  const docTotal = useMemo(() => {
    if (!doc) return 0;
    return (doc.leak_areas || [])
      .filter(a => a.client_status !== 'declined')
      .reduce((s, a) => s + areaTotal(a), 0);
  }, [doc]);

  const save = async () => {
    if (!doc) return;
    setSaving(true);
    try {
      const res = await api.put(`/remedial-repairs/appraisals/${appraisalId}`, {
        leak_areas: doc.leak_areas,
        status: doc.status || 'draft',
        discount_pct: doc.discount_pct || 0,
      });
      setDoc(res.data);
      toast.success('Remedial Repairs saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !doc) {
    return (
      <div className="flex items-center justify-center py-20 text-slate-500">
        <Loader2 size={24} className="animate-spin mr-2" /> Loading Remedial Repairs…
      </div>
    );
  }

  const areas = doc.leak_areas || [];
  const pendingCount = areas.filter(a => a.client_status === 'pending').length;
  const approvedCount = areas.filter(a => a.client_status === 'approved').length;
  const declinedCount = areas.filter(a => a.client_status === 'declined').length;

  return (
    <div className="space-y-4" data-testid="remedial-repairs-builder">
      <div className="flex justify-end">
        <SaveDocButton
          appraisalId={appraisalId}
          docKind="quotation_detailed"
          label="Save Quote PDF"
          expandChildren={false}
          variant="outline"
          size="sm"
          testId="remedial-save-quote-pdf"
        />
      </div>
      {/* Header */}
      <div className="bg-white border border-rose-200 rounded-lg p-4 flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[240px]">
          <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
            <AlertTriangle size={18} className="text-rose-500" />
            Remedial Repairs
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Localised leak-area repairs · the client picks which areas to authorise from a checklist.
            {roofType && <> · Roof type: <strong className="text-slate-700">{roofType}</strong></>}
          </p>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <span data-testid="rr-summary-pending" className="px-2 py-1 rounded bg-amber-100 text-amber-800 font-bold">{pendingCount} pending</span>
          <span data-testid="rr-summary-approved" className="px-2 py-1 rounded bg-emerald-100 text-emerald-800 font-bold">{approvedCount} approved</span>
          {declinedCount > 0 && <span data-testid="rr-summary-declined" className="px-2 py-1 rounded bg-slate-100 text-slate-600 font-bold">{declinedCount} declined</span>}
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-wider text-slate-400">Quote total</div>
          <div data-testid="rr-doc-total" className="text-xl font-bold text-rose-600 font-mono-num">{fmt(docTotal)}</div>
        </div>
        <button
          type="button"
          data-testid="rr-save"
          onClick={save}
          disabled={saving}
          className="inline-flex items-center gap-2 px-4 py-2 rounded bg-rose-600 text-white text-sm font-bold hover:bg-rose-700 disabled:opacity-50"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          Save
        </button>
        {/* Iter165 Phase B — Customer PDF download. Renders only
            leak areas with client_status != 'declined' and photos
            with is_client_facing=true. Markup applied per spec 6a. */}
        <button
          type="button"
          data-testid="rr-customer-pdf"
          onClick={() => window.open(`${api.defaults.baseURL}/remedial-repairs/appraisals/${appraisalId}/quote.pdf`, '_blank')}
          disabled={areas.length === 0}
          className="inline-flex items-center gap-2 px-3 py-2 rounded bg-white border border-rose-300 text-rose-700 text-sm font-bold hover:bg-rose-50 disabled:opacity-50"
          title="Generate the customer-facing Remedial Repairs PDF"
        >
          <FileText size={14} />
          Customer PDF
        </button>
      </div>

      {/* Iter167 — Roof Details quick-edit panel under the Remedial Repairs banner. */}
      <BuilderRoofDetailsPanel api={api} appraisalId={appraisalId} onApplied={load} />

      {/* Leak area cards */}
      {areas.length === 0 && (
        <div className="bg-white border-2 border-dashed border-rose-200 rounded-lg p-8 text-center" data-testid="rr-empty-state">
          <AlertTriangle size={28} className="text-rose-300 mx-auto mb-2" />
          <p className="text-sm text-slate-600 mb-1 font-semibold">Ready to add Remedial Repairs.</p>
          <p className="text-xs text-slate-500 mb-3">
            You can create leak-area repairs here without a diagram pin.
            Add one now to get started, or drop a leak pin on the diagram to link it back to this area.
          </p>
          <button
            type="button"
            data-testid="rr-add-area-empty"
            onClick={addArea}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded bg-rose-600 text-white text-sm font-bold hover:bg-rose-700"
          >
            <Plus size={14} /> Add leak area
          </button>
        </div>
      )}

      {areas.map((area) => {
        const isOpen = !!expanded[area.area_id];
        const total = areaTotal(area);
        const clientStatus = area.client_status || 'pending';
        return (
          <div
            key={area.area_id}
            data-testid={`rr-area-${area.area_id}`}
            className={`bg-white border rounded-lg overflow-hidden ${
              clientStatus === 'approved' ? 'border-emerald-300 ring-1 ring-emerald-200' :
              clientStatus === 'declined' ? 'border-slate-200 opacity-60' :
              'border-rose-200'
            }`}
          >
            {/* Area header */}
            <div className="flex items-center gap-3 px-4 py-2.5 bg-rose-50/40">
              <button
                type="button"
                data-testid={`rr-area-toggle-${area.area_id}`}
                onClick={() => setExpanded(e => ({ ...e, [area.area_id]: !isOpen }))}
                className="text-slate-500 hover:text-rose-600"
              >
                {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>
              <input
                data-testid={`rr-area-label-${area.area_id}`}
                value={area.label || ''}
                onChange={e => updateArea(area.area_id, { label: e.target.value })}
                placeholder="Area label"
                className="text-sm font-bold text-slate-800 bg-transparent border-b border-transparent hover:border-rose-200 focus:border-rose-500 focus:outline-none flex-1 max-w-xs"
              />
              {/* Client status chips */}
              <div className="inline-flex gap-1">
                {[
                  { v: 'pending',  label: 'Pending',  cls: 'bg-amber-100 text-amber-800',   activeCls: 'bg-amber-500 text-white' },
                  { v: 'approved', label: 'Approved', cls: 'bg-emerald-100 text-emerald-800', activeCls: 'bg-emerald-600 text-white' },
                  { v: 'declined', label: 'Declined', cls: 'bg-slate-200 text-slate-700',    activeCls: 'bg-slate-500 text-white' },
                ].map(s => (
                  <button
                    key={s.v}
                    type="button"
                    data-testid={`rr-area-status-${s.v}-${area.area_id}`}
                    onClick={() => updateArea(area.area_id, { client_status: s.v })}
                    className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${clientStatus === s.v ? s.activeCls : s.cls + ' hover:opacity-80'}`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
              <span data-testid={`rr-area-total-${area.area_id}`} className="text-sm font-bold font-mono-num text-rose-700 min-w-[70px] text-right">{fmt(total)}</span>
              <button
                type="button"
                data-testid={`rr-area-remove-${area.area_id}`}
                onClick={() => removeArea(area.area_id)}
                className="p-1 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded"
                title="Remove this leak area"
              >
                <Trash2 size={14} />
              </button>
            </div>

            {/* Area body */}
            {isOpen && (
              <div className="p-4 space-y-3">
                {/* Iter161 Phase B — Diagram pin link row. Operator
                    can "Place on diagram" to jump to Roof Plan in pin-
                    drop mode, OR clear an existing pin. When a pin is
                    placed, the chip shows the coordinates + clicks
                    through to focus the pin on the diagram. */}
                {(() => {
                  const pin = findPin(area);
                  const placing = placingPinFor === area.area_id;
                  return (
                    <div className="flex items-center gap-2 flex-wrap pb-2 border-b border-rose-100">
                      <span className="text-[11px] font-bold uppercase tracking-wider text-rose-700 flex items-center gap-1">
                        <MapPin size={11} /> Diagram pin
                      </span>
                      {pin ? (
                        <>
                          <button
                            type="button"
                            data-testid={`rr-area-pin-focus-${area.area_id}`}
                            onClick={() => onJumpToPin?.(pin.id, 'LeakArea')}
                            className="inline-flex items-center gap-1 text-[11px] px-2 py-1 bg-rose-50 text-rose-800 border border-rose-200 rounded hover:bg-rose-100 font-mono"
                            title="Show pin on Roof Plan"
                          >
                            <span className="text-rose-600">⚠</span>
                            placed at {Number(pin.x_pct).toFixed(1)}%, {Number(pin.y_pct).toFixed(1)}%
                          </button>
                          <button
                            type="button"
                            data-testid={`rr-area-pin-place-${area.area_id}`}
                            onClick={() => startPlacingPin(area)}
                            className="text-[10px] text-slate-500 hover:text-rose-700 underline"
                            title="Replace this pin"
                          >
                            re-place
                          </button>
                          <button
                            type="button"
                            data-testid={`rr-area-pin-remove-${area.area_id}`}
                            onClick={() => removePin(area)}
                            className="text-[10px] text-slate-500 hover:text-rose-700 underline"
                          >
                            remove
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          data-testid={`rr-area-pin-place-${area.area_id}`}
                          onClick={() => startPlacingPin(area)}
                          disabled={placing}
                          className="inline-flex items-center gap-1 text-[11px] px-2 py-1 bg-white text-rose-700 border border-rose-300 rounded hover:bg-rose-50 disabled:opacity-50"
                        >
                          <MapPin size={11} />
                          {placing ? 'Switch to Roof Plan to drop…' : 'Place on diagram'}
                        </button>
                      )}
                      <span className="text-[10px] text-slate-400 italic ml-auto">Tradespack PDFs will render the leak pin alongside Ridge / Valley pins.</span>
                    </div>
                  );
                })()}
                {/* Iter161 — Location · Repair Type · Description
                    grid. Each preset list is editable via the admin
                    page at /admin/repair-presets; cascade flips off
                    per-area via the small "Override cascade" toggle
                    in the section header. Iter166 — each row now has
                    its own "Edit list" deep-link AND a visible popover
                    dropdown so the operator can browse presets without
                    relying on the browser's native datalist UX. */}
                <div className="flex items-center justify-end gap-2 -mb-1 flex-wrap">
                  <label className="text-[10px] text-slate-500 inline-flex items-center gap-1">
                    <input
                      type="checkbox"
                      data-testid={`rr-area-override-${area.area_id}`}
                      checked={!!cascadeOverride[area.area_id]}
                      onChange={(e) => setCascadeOverride(c => ({ ...c, [area.area_id]: e.target.checked }))}
                      className="rounded"
                    />
                    Override cascade — show full lists
                  </label>
                  <Link
                    to="/admin/repair-presets"
                    target="_blank"
                    data-testid={`rr-area-manage-presets-${area.area_id}`}
                    className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-1 rounded border border-rose-300 text-rose-700 bg-white hover:bg-rose-50"
                    title="Manage Location / Repair Type / Description presets and their cascade links"
                  >
                    <Settings size={11} /> Edit preset lists & cascade
                  </Link>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <CascadeSelect
                    testid={`rr-area-location-${area.area_id}`}
                    icon={<MapPin size={10} />}
                    label="Location"
                    value={area.location_text || ''}
                    options={locationPresets}
                    onChange={(v) => updateArea(area.area_id, { location_text: v })}
                    onInlineAdd={(v) => inlineAddPreset('location', v)}
                    editKind="location"
                  />
                  <CascadeSelect
                    testid={`rr-area-repair-type-${area.area_id}`}
                    label="Repair type"
                    value={area.repair_type || ''}
                    options={repairTypePresetsFor(area.area_id)}
                    onChange={(v) => updateArea(area.area_id, { repair_type: v })}
                    onInlineAdd={(v) => inlineAddPreset('repair_type', v, { parent_roof_type: roofType })}
                    editKind="repair_type"
                  />
                  <CascadeSelect
                    testid={`rr-area-description-${area.area_id}`}
                    icon={<FileText size={10} />}
                    label="Description"
                    value={area.description || ''}
                    options={descriptionPresetsFor(area.area_id, area.repair_type || '')}
                    onChange={(v) => updateArea(area.area_id, { description: v })}
                    onInlineAdd={(v) => inlineAddPreset('description', v, { parent_repair_type: area.repair_type })}
                    editKind="description"
                  />
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Notes</label>
                  <textarea
                    data-testid={`rr-area-notes-${area.area_id}`}
                    value={area.notes || ''}
                    onChange={e => updateArea(area.area_id, { notes: e.target.value })}
                    placeholder="Free notes — context, materials needed, special instructions…"
                    rows={2}
                    className="w-full mt-0.5 text-sm px-2 py-1.5 border border-slate-300 rounded resize-y"
                  />
                </div>

                {/* Repair lines */}
                <div className="mt-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Repair lines ({(area.repair_lines || []).length})</span>
                    <button
                      type="button"
                      data-testid={`rr-line-add-${area.area_id}`}
                      onClick={() => addLine(area.area_id)}
                      className="inline-flex items-center gap-1 text-[11px] font-bold text-rose-700 bg-rose-50 hover:bg-rose-100 px-2 py-0.5 rounded border border-rose-200"
                    >
                      <Plus size={11} /> Add line
                    </button>
                  </div>
                  {(area.repair_lines || []).length === 0 ? (
                    <div className="text-[11px] text-slate-400 italic px-2 py-3 border border-dashed border-slate-200 rounded text-center">No lines yet — add one to allocate components for this leak area.</div>
                  ) : (
                    <div className="space-y-1.5">
                      {(area.repair_lines || []).map((l) => {
                        const cascaded = cascadedRatesFor(l);
                        const lineOverride = !!lineCascadeOverride[l.line_id];
                        // Distinct profile / material options that match
                        // the current Component Type filter — let the
                        // operator narrow the picker step-by-step
                        // before stamping a Type.
                        const profilesForCT = Array.from(new Set(
                          (l.component_type
                            ? allRates.filter(r => (r._category || r.component_type || '').toLowerCase() === l.component_type.toLowerCase())
                            : allRates
                          ).map(r => r.profile).filter(Boolean)
                        )).sort();
                        const materialsForCTProfile = Array.from(new Set(
                          allRates
                            .filter(r => !l.component_type || (r._category || r.component_type || '').toLowerCase() === l.component_type.toLowerCase())
                            .filter(r => !l.profile || r.profile === l.profile)
                            .map(r => r.material).filter(Boolean)
                        )).sort();
                        return (
                        <div key={l.line_id} data-testid={`rr-line-${l.line_id}`} className="bg-slate-50/50 px-2 py-2 rounded border border-slate-100 space-y-1.5">
                          {/* Iter161 — Component Type cascade row.
                              Operator picks Component Type → Type
                              (narrowed by CT) → Profile (narrowed by
                              CT+Type) → Material (narrowed further).
                              Override toggle shows the unfiltered
                              Type list.
                              Iter167 — column order changed to
                              CT → Type → Profile → Material per
                              operator request, and CT width halved
                              (col-span-2 from 3) so the Type field
                              has more room. */}
                          <div className="grid grid-cols-12 gap-1.5 items-end">
                            <div className="col-span-12 md:col-span-2">
                              <label className="text-[9px] font-bold uppercase tracking-wider text-slate-400">Component type</label>
                              <select
                                data-testid={`rr-line-ctype-${l.line_id}`}
                                value={l.component_type || ''}
                                onChange={(e) => updateLine(area.area_id, l.line_id, {
                                  component_type: e.target.value,
                                  // Reset narrower fields when changing the parent so the operator doesn't keep stale picks
                                  profile: '', material: '', type_name: '',
                                })}
                                className="w-full text-xs px-1.5 py-1 border border-slate-300 rounded bg-white"
                              >
                                <option value="">— Pick —</option>
                                {componentTypeOptions.map(c => <option key={c} value={c}>{c}</option>)}
                              </select>
                            </div>
                            <div className="col-span-12 md:col-span-5">
                              <label className="text-[9px] font-bold uppercase tracking-wider text-slate-400 flex items-center justify-between">
                                <span>Type ({cascaded.length} match{cascaded.length === 1 ? '' : 'es'})</span>
                                <label className="inline-flex items-center gap-1 normal-case tracking-normal text-[9px] text-slate-400 font-normal">
                                  <input
                                    type="checkbox"
                                    data-testid={`rr-line-override-${l.line_id}`}
                                    checked={lineOverride}
                                    onChange={(e) => setLineCascadeOverride(c => ({ ...c, [l.line_id]: e.target.checked }))}
                                    className="rounded scale-75"
                                  />
                                  show all
                                </label>
                              </label>
                              <input
                                list={`rr-types-${l.line_id}`}
                                data-testid={`rr-line-type-${l.line_id}`}
                                value={l.type_name || ''}
                                onChange={(e) => pickTypeName(area.area_id, l.line_id, e.target.value)}
                                placeholder={(l.component_type && cascaded.length > 0) ? 'Pick from cascaded list…' : 'Pick or type…'}
                                className="w-full text-xs px-1.5 py-1 border border-slate-300 rounded"
                              />
                              <datalist id={`rr-types-${l.line_id}`}>
                                {cascaded.map((r, idx) => (
                                  <option
                                    key={`${r._category}|${r.type_name}|${r.profile || ''}|${r.material || ''}|${idx}`}
                                    value={r.type_name}
                                  />
                                ))}
                              </datalist>
                            </div>
                            <div className="col-span-6 md:col-span-2">
                              <label className="text-[9px] font-bold uppercase tracking-wider text-slate-400">Profile</label>
                              <select
                                data-testid={`rr-line-profile-${l.line_id}`}
                                value={l.profile || ''}
                                onChange={(e) => updateLine(area.area_id, l.line_id, { profile: e.target.value, material: '', type_name: '' })}
                                className="w-full text-xs px-1.5 py-1 border border-slate-300 rounded bg-white"
                                disabled={!l.component_type && !lineOverride}
                              >
                                <option value="">— Any —</option>
                                {profilesForCT.map(p => <option key={p} value={p}>{p}</option>)}
                              </select>
                            </div>
                            <div className="col-span-6 md:col-span-2">
                              <label className="text-[9px] font-bold uppercase tracking-wider text-slate-400">Material</label>
                              <select
                                data-testid={`rr-line-material-${l.line_id}`}
                                value={l.material || ''}
                                onChange={(e) => updateLine(area.area_id, l.line_id, { material: e.target.value, type_name: '' })}
                                className="w-full text-xs px-1.5 py-1 border border-slate-300 rounded bg-white"
                                disabled={!l.component_type && !lineOverride}
                              >
                                <option value="">— Any —</option>
                                {materialsForCTProfile.map(m => <option key={m} value={m}>{m}</option>)}
                              </select>
                            </div>
                            <div className="col-span-12 md:col-span-1 flex justify-end pb-0.5">
                              <button
                                type="button"
                                data-testid={`rr-line-remove-${l.line_id}`}
                                onClick={() => removeLine(area.area_id, l.line_id)}
                                className="text-slate-400 hover:text-rose-600 p-1"
                                title="Remove line"
                              >
                                <X size={12} />
                              </button>
                            </div>
                          </div>
                          {/* Qty / Length / Rates / Total row */}
                          <div className="grid grid-cols-12 gap-1.5 items-end">
                            <div className="col-span-3 md:col-span-2">
                              <label className="text-[9px] font-bold uppercase tracking-wider text-slate-400">Qty</label>
                              <input
                                type="number" step="0.01" min="0"
                                data-testid={`rr-line-qty-${l.line_id}`}
                                value={l.quantity ?? 0}
                                onChange={e => updateLine(area.area_id, l.line_id, { quantity: parseFloat(e.target.value) || 0 })}
                                className="w-full text-xs px-1 py-1 border border-slate-300 rounded font-mono-num text-right"
                              />
                            </div>
                            <div className="col-span-3 md:col-span-2">
                              <label className="text-[9px] font-bold uppercase tracking-wider text-slate-400">Length</label>
                              <input
                                type="number" step="0.01" min="0"
                                data-testid={`rr-line-length-${l.line_id}`}
                                value={l.length ?? 0}
                                onChange={e => updateLine(area.area_id, l.line_id, { length: parseFloat(e.target.value) || 0 })}
                                className="w-full text-xs px-1 py-1 border border-slate-300 rounded font-mono-num text-right"
                              />
                            </div>
                            <div className="col-span-3 md:col-span-3">
                              <label className="text-[9px] font-bold uppercase tracking-wider text-slate-400">Mat $/u</label>
                              <input
                                type="number" step="0.01" min="0"
                                data-testid={`rr-line-mat-rate-${l.line_id}`}
                                value={l.materials_rate ?? 0}
                                onChange={e => updateLine(area.area_id, l.line_id, { materials_rate: parseFloat(e.target.value) || 0 })}
                                className="w-full text-xs px-1 py-1 border border-slate-300 rounded font-mono-num text-right"
                              />
                            </div>
                            <div className="col-span-3 md:col-span-3">
                              <label className="text-[9px] font-bold uppercase tracking-wider text-slate-400">Lab $/u</label>
                              <input
                                type="number" step="0.01" min="0"
                                data-testid={`rr-line-lab-rate-${l.line_id}`}
                                value={l.labour_rate ?? 0}
                                onChange={e => updateLine(area.area_id, l.line_id, { labour_rate: parseFloat(e.target.value) || 0 })}
                                className="w-full text-xs px-1 py-1 border border-slate-300 rounded font-mono-num text-right"
                              />
                            </div>
                            <div className="col-span-12 md:col-span-2">
                              <label className="text-[9px] font-bold uppercase tracking-wider text-slate-400">Total</label>
                              <div data-testid={`rr-line-total-${l.line_id}`} className="text-xs font-bold font-mono-num text-rose-700 px-1 py-1 bg-white border border-slate-200 rounded text-right">
                                {fmt(lineTotal(l))}
                              </div>
                            </div>
                          </div>
                        </div>
                      );})}
                    </div>
                  )}
                </div>

                {/* Iter164 — Photos strip per leak area. Operator
                    attaches Defect / Before / etc. photos from the
                    appraisal gallery or uploads new ones; selected
                    customer-facing photos flow into the Remedial
                    Repairs PDF (Phase B) and customer portal. */}
                <LeakAreaPhotoStrip api={api} appraisalId={appraisalId} area={area} />
              </div>
            )}
          </div>
        );
      })}

      {/* Add area button (bottom, always visible) */}
      {areas.length > 0 && (
        <button
          type="button"
          data-testid="rr-add-area"
          onClick={addArea}
          className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded border-2 border-dashed border-rose-300 text-rose-600 text-sm font-bold hover:bg-rose-50"
        >
          <Plus size={14} /> Add another leak area
        </button>
      )}
    </div>
  );
}


/**
 * Iter161 — CascadeSelect.
 * Iter165 fix — replaced the native <input list=/datalist> combo with
 * a custom popover dropdown so the list is ALWAYS visible on click
 * (datalist UX is browser-dependent and felt invisible to operators).
 * Free-text input is still allowed; if the typed value isn't in the
 * preset list, an inline "+ Save as new preset" button appears.
 * Optionally renders an "Edit list" pencil button that deep-links to
 * the Remedial Repair Presets admin page with the right tab focused.
 *
 * Used for the Location / Repair Type / Description fields inside
 * each leak-area card.
 */
function CascadeSelect({ testid, icon, label, value, options, onChange, onInlineAdd, editKind }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const hasValue = !!value;
  const isInList = options.includes(value);
  const canSaveAsPreset = hasValue && !isInList;
  const needle = (query || '').trim().toLowerCase();
  const filtered = needle
    ? options.filter(o => o.toLowerCase().includes(needle))
    : options;

  return (
    <div ref={wrapRef} className="relative">
      <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1 justify-between">
        <span className="inline-flex items-center gap-1">{icon}{label}</span>
        {editKind && (
          <a
            href={`/admin/repair-presets?kind=${editKind}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`${testid}-edit-list`}
            className="inline-flex items-center gap-0.5 text-[9px] text-slate-400 hover:text-rose-700 normal-case font-semibold"
            title={`Edit the ${label} list & cascade links`}
            onClick={e => e.stopPropagation()}
          >
            <Edit3 size={9} /> Edit list
          </a>
        )}
      </label>
      <div className="flex items-stretch gap-1 mt-0.5">
        <div className="flex-1 min-w-0 flex items-stretch border border-slate-300 rounded bg-white focus-within:ring-1 focus-within:ring-rose-400">
          <input
            data-testid={testid}
            value={value || ''}
            onChange={(e) => { onChange(e.target.value); setQuery(e.target.value); if (!open) setOpen(true); }}
            onFocus={() => setOpen(true)}
            onClick={() => setOpen(true)}
            placeholder={`Pick or type ${label.toLowerCase()}…`}
            className="flex-1 min-w-0 text-sm px-2 py-1.5 outline-none bg-transparent"
          />
          <button
            type="button"
            data-testid={`${testid}-toggle`}
            onClick={() => setOpen(o => !o)}
            className="px-1.5 text-slate-500 hover:text-rose-700 border-l border-slate-200"
            tabIndex={-1}
            aria-label={`Toggle ${label} options`}
          >
            <ChevronDown size={14} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
          </button>
        </div>
        {canSaveAsPreset && onInlineAdd && (
          <button
            type="button"
            data-testid={`${testid}-add`}
            onClick={() => onInlineAdd(value)}
            className="px-2 text-[10px] font-bold text-rose-700 bg-rose-50 hover:bg-rose-100 border border-rose-200 rounded inline-flex items-center gap-0.5"
            title="Save this as a new preset"
          >
            <Plus size={10} /> Save
          </button>
        )}
      </div>
      {open && (
        <div
          data-testid={`${testid}-listbox`}
          className="absolute z-30 left-0 right-0 mt-1 bg-white border border-slate-200 rounded shadow-lg max-h-56 overflow-y-auto"
        >
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-[11px] italic text-slate-400">
              No presets {needle ? `match "${query}"` : 'yet'}.
              {editKind && (
                <>
                  {' '}
                  <a
                    href={`/admin/repair-presets?kind=${editKind}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-rose-700 underline"
                  >
                    Add some here
                  </a>.
                </>
              )}
            </div>
          ) : filtered.map(o => (
            <button
              key={o}
              type="button"
              data-testid={`${testid}-opt-${o.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`}
              onClick={() => { onChange(o); setOpen(false); setQuery(''); }}
              className={`w-full text-left px-3 py-1.5 text-sm hover:bg-rose-50 ${
                o === value ? 'bg-rose-50 text-rose-800 font-semibold' : 'text-slate-700'
              }`}
            >
              {o}
            </button>
          ))}
          {editKind && filtered.length > 0 && (
            <a
              href={`/admin/repair-presets?kind=${editKind}`}
              target="_blank"
              rel="noopener noreferrer"
              className="block border-t border-slate-100 px-3 py-1.5 text-[11px] font-semibold text-slate-500 hover:text-rose-700 hover:bg-slate-50"
            >
              <Edit3 size={10} className="inline -mt-0.5 mr-1" />
              Manage list & cascade links…
            </a>
          )}
        </div>
      )}
    </div>
  );
}
